import React, { useState } from 'react';
import { LoadingSpinner } from '../../ui/IconComponents';
import { UploadingVideo } from '@/utils/optimistic/optimisticUpload';

// Model definitions for different upload types
const VIDEO_MODELS = ['Veo 3.1', 'Veo 3.1 Fast', 'Veo 3', 'Veo 3 Fast', 'Veo 2', 'Hunyuan', 'Flow'] as const;
const IMAGE_MODELS = ['Gemini 2.5'] as const;
const TRANSCRIPT_MODELS = ['Whisper'] as const;

type UploadType = 'video' | 'image' | 'transcript';
type VideoModel = typeof VIDEO_MODELS[number];
type ImageModel = typeof IMAGE_MODELS[number];
type TranscriptModel = typeof TRANSCRIPT_MODELS[number];
type UploadModel = VideoModel | ImageModel | TranscriptModel;

interface UploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  uploadType: UploadType;
  onUpload?: (file: File, prompt: string, model: string) => Promise<void>;
  isUploading?: boolean;
  uploadError?: string | null;
  uploadingVideos?: UploadingVideo[];
}

const UploadModal: React.FC<UploadModalProps> = ({
  isOpen,
  onClose,
  uploadType,
  onUpload,
  isUploading = false,
  uploadError = null,
  uploadingVideos = [],
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [prompt, setPrompt] = useState('');
  const [selectedModel, setSelectedModel] = useState<UploadModel | ''>('');
  const [customModel, setCustomModel] = useState('');
  const [isOtherModel, setIsOtherModel] = useState(false);

  // Get models and default based on upload type
  const getModels = (): readonly string[] => {
    switch (uploadType) {
      case 'video':
        return VIDEO_MODELS;
      case 'image':
        return IMAGE_MODELS;
      case 'transcript':
        return TRANSCRIPT_MODELS;
      default:
        return [];
    }
  };

  const getDefaultModel = (): UploadModel | '' => {
    switch (uploadType) {
      case 'video':
        return 'Flow';
      case 'image':
        return 'Gemini 2.5';
      case 'transcript':
        return 'Whisper';
      default:
        return '';
    }
  };

  // Initialize model if not set
  React.useEffect(() => {
    if (!selectedModel) {
      setSelectedModel(getDefaultModel());
    }
  }, [uploadType]);

  const getFileAccept = (): string => {
    switch (uploadType) {
      case 'video':
        return 'video/*';
      case 'image':
        return 'image/*';
      case 'transcript':
        return '.txt,.srt,.vtt,.ass,.ssa,.sub,.sbv,.json,.xml,.ttml,.dfxp,.stl';
      default:
        return '';
    }
  };

  const getTitle = (): string => {
    switch (uploadType) {
      case 'video':
        return 'Upload Video';
      case 'image':
        return 'Upload Image';
      case 'transcript':
        return 'Upload Transcript';
      default:
        return 'Upload';
    }
  };

  const getFileLabel = (): string => {
    switch (uploadType) {
      case 'video':
        return 'Video File';
      case 'image':
        return 'Image File';
      case 'transcript':
        return 'Transcript File';
      default:
        return 'File';
    }
  };

  const getPlaceholderText = (): string => {
    switch (uploadType) {
      case 'video':
        return 'MP4, MOV, AVI up to 100MB';
      case 'image':
        return 'Upload Image (up to 10MB)';
      case 'transcript':
        return 'Upload transcript file';
      default:
        return 'Upload file';
    }
  };

  const getUploadButtonText = (): string => {
    if (isUploading) {
      return 'Uploading...';
    }
    return getTitle();
  };

  const isPromptRequired = (): boolean => {
    return uploadType !== 'transcript';
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    let isValid = false;
    let errorMessage = '';

    if (uploadType === 'video') {
      isValid = file.type.startsWith('video/');
      errorMessage = 'Please select a valid video file.';
    } else if (uploadType === 'image') {
      isValid = file.type.startsWith('image/');
      errorMessage = 'Please select a valid image file.';
    } else if (uploadType === 'transcript') {
      const validExtensions = ['.txt', '.srt', '.vtt', '.ass', '.ssa', '.sub', '.sbv', '.json', '.xml', '.ttml', '.dfxp', '.stl'];
      const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
      isValid = validExtensions.includes(fileExtension);
      errorMessage = 'Please select a valid transcript file (.txt, .srt, .vtt, .ass, .ssa, .sub, .sbv, .json, .xml, .ttml, .dfxp, .stl).';
    }

    if (isValid) {
      setSelectedFile(file);
    } else {
      alert(errorMessage);
    }
  };

  const handleUploadSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFile || !onUpload) return;

    // Validate prompt for video/image uploads
    if (isPromptRequired() && !prompt.trim()) return;

    const finalModel = isOtherModel ? customModel.trim() : selectedModel;
    if (!finalModel) return;

    const uploadPrompt = isPromptRequired() ? prompt.trim() : '';

    // Close modal immediately and upload in background for all types
    setSelectedFile(null);
    setPrompt('');
    setSelectedModel(getDefaultModel());
    setCustomModel('');
    setIsOtherModel(false);
    onClose();
    
    // Upload in background
    onUpload(selectedFile, uploadPrompt, finalModel).catch(error => {
      console.error('Upload failed:', error);
    });
  };

  const handleModelChange = (value: string) => {
    if (value === 'Other') {
      setIsOtherModel(true);
      setSelectedModel(getDefaultModel());
    } else {
      setIsOtherModel(false);
      setCustomModel('');
      setSelectedModel(value as UploadModel);
    }
  };

  const handleClose = () => {
    if (!isUploading) {
      setSelectedFile(null);
      setPrompt('');
      setSelectedModel(getDefaultModel());
      setCustomModel('');
      setIsOtherModel(false);
      onClose();
    }
  };

  if (!isOpen) return null;

  const models = getModels();
  const requiresPrompt = isPromptRequired();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-md w-full p-6">
        {/* Modal Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900">{getTitle()}</h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors bg-white focus:outline-none outline-none"
            disabled={isUploading}
            style={{ outline: 'none' }}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Upload Form */}
        <form onSubmit={handleUploadSubmit} className="space-y-4">
          {/* File Input */}
          <div>
            <label htmlFor="file-input" className="block text-sm font-medium text-gray-700 mb-2">
              {getFileLabel()} <span className="text-red-500">*</span>
            </label>
            <button
              type="button"
              className="w-full border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors cursor-pointer bg-white focus:outline-none outline-none"
              onClick={() => !isUploading && document.getElementById('file-input')?.click()}
              disabled={isUploading}
              style={{ outline: 'none' }}
            >
              {selectedFile ? (
                <div className="space-y-2">
                  <div className="text-sm text-gray-600 truncate max-w-full px-2">Selected: {selectedFile.name}</div>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedFile(null);
                      const fileInput = document.getElementById('file-input') as HTMLInputElement;
                      if (fileInput) {
                        fileInput.value = '';
                      }
                    }}
                    className="text-blue-600 hover:text-blue-700 text-sm underline bg-white focus:outline-none outline-none"
                    disabled={isUploading}
                    style={{ outline: 'none' }}
                  >
                    Change file
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="text-gray-400 mb-2">
                    <svg className="mx-auto h-12 w-12" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                    </svg>
                  </div>
                  <div className="text-sm text-gray-500">{getPlaceholderText()}</div>
                </div>
              )}
            </button>
            <input
              id="file-input"
              type="file"
              accept={getFileAccept()}
              onChange={handleFileChange}
              className="hidden"
              disabled={isUploading}
              required
            />
          </div>

          {/* Prompt Input - Only for video and image uploads */}
          {requiresPrompt && (
            <div>
              <label htmlFor="prompt-input" className="block text-sm font-medium text-gray-700 mb-2">
                Prompt <span className="text-red-500">*</span>
              </label>
              <textarea
                id="prompt-input"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder={`Enter the prompt used to generate this ${uploadType}...`}
                className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                rows={3}
                disabled={isUploading}
                required
              />
            </div>
          )}

          {/* AI Model Input */}
          <div>
            <label htmlFor="model-select" className="block text-sm font-medium text-gray-700 mb-2">
              AI Model <span className="text-red-500">*</span>
            </label>
            <select
              id="model-select"
              value={isOtherModel ? 'Other' : selectedModel}
              onChange={(e) => handleModelChange(e.target.value)}
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              disabled={isUploading}
              required
            >
              {models.map((model: string) => (
                <option key={model} value={model}>
                  {model}
                </option>
              ))}
              <option value="Other">Other</option>
            </select>
            
            {/* Custom Model Input - only show when "Other" is selected */}
            {isOtherModel && (
              <div className="mt-2">
                <input
                  type="text"
                  value={customModel}
                  onChange={(e) => setCustomModel(e.target.value)}
                  placeholder="Enter custom AI model name..."
                  className="w-full bg-white px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={isUploading}
                  required
                />
              </div>
            )}
          </div>

          {/* Error Display */}
          {uploadError && (
            <div className="text-red-600 text-sm p-2 bg-red-50 rounded">
              {uploadError}
            </div>
          )}

          {/* Submit Button */}
          <div className="flex items-center justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={isUploading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={
                !selectedFile || 
                (requiresPrompt && !prompt.trim()) || 
                (isOtherModel && !customModel.trim()) || 
                isUploading
              }
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {isUploading && <LoadingSpinner className="w-4 h-4" />}
              <span>{getUploadButtonText()}</span>
            </button>
          </div>
        </form>

        {/* Current Uploads - Only for video uploads */}
        {uploadType === 'video' && uploadingVideos.length > 0 && (
          <div className="mt-6 pt-4 border-t">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Current Uploads</h3>
            <div className="space-y-2">
              {uploadingVideos.map((video) => (
                <div key={video.id} className="flex items-center space-x-3 text-sm">
                  <LoadingSpinner className="w-4 h-4 text-blue-600" />
                  <span className="flex-1 text-gray-700">{video.prompt}</span>
                  <span className="text-gray-500">{video.progress}%</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UploadModal;
